import LockResetRoundedIcon from '@mui/icons-material/LockResetRounded';
import { LoadingButton } from '@mui/lab';
import { Alert, TextField, Typography, useTheme } from '@mui/material';
import { useCallback, useContext, useState } from 'react';
import AuthApi from '../../../apis/AuthApi';
import SnackbarContext from '../../../contexts/SnackbarContext';
import useApi from '../../../lib/useApi';

/**
 * The properties for the forgot password form
 */
interface ForgotPasswordFormProps {
  /**
   * Set that the forgot password form has been successfully submitted
   */
  setSubmitted: () => void;
}

/**
 * This component is the form to show the user to initiate the password recovery
 */
const ForgotPasswordForm = (props: ForgotPasswordFormProps) => {
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [formError, setFormError] = useState(false);
  const [email, setEmail] = useState('');
  const authApi = useApi(AuthApi);
  const snackbar = useContext(SnackbarContext);

  /**
   * Send the reset password request
   */
  const resetPassword = useCallback(() => {
    if (email == '') {
      setFormError(true);
      return;
    }
    setFormError(false);
    setIsLoading(true);
    authApi
      .ForgotPassword({ email: email })
      .then(() => {
        props.setSubmitted();
      })
      .catch(() => {
        snackbar.setAlert(
          <Alert variant="filled" severity="error">
            An error occurred while sending the forget password request.
          </Alert>
        );
        snackbar.triggerSnackbar();
      })
      .finally(() => setIsLoading(false));
  }, [authApi, email, props, snackbar]);

  return (
    <>
      <LockResetRoundedIcon sx={{ fontSize: '4em', marginY: '.25em' }} />
      <Typography
        variant="h1"
        fontSize="1.5em"
        fontWeight="bold"
        gutterBottom
        textAlign="center"
      >
        Forgot your password?
      </Typography>
      <Typography
        color={theme.palette.text.secondary}
        textAlign="center"
        sx={{ maxWidth: '90%', marginBottom: '2em' }}
      >
        Enter your email and we&apos;ll send you a link to reset your password.
      </Typography>
      <TextField
        label="Email"
        type="email"
        fullWidth
        disabled={isLoading}
        sx={{ marginBottom: '1em' }}
        error={formError}
        helperText={formError && 'An email is required'}
        onChange={(e) => setEmail(e.currentTarget.value)}
      />
      <LoadingButton
        variant="contained"
        fullWidth
        sx={{ marginBottom: '1em' }}
        loading={isLoading}
        onClick={resetPassword}
      >
        Reset Password
      </LoadingButton>
    </>
  );
};

export default ForgotPasswordForm;
